import api from "./api";

export type StockTransferStatus =
  | "DRAFT"
  | "PENDING"
  | "DISPATCHED"
  | "RECEIVED"
  | "CANCELLED";

export type StockTransferItem = {
  id: string;
  productId: string;
  unit?: string | null;
  packSize?: number;
  conversionFactor?: number;
  baseQuantity?: number | null;
  quantityRequested: number;
  quantitySent: number;
  quantityReceived: number;
  notes?: string | null;
  product?: {
    id: string;
    name: string;
    sku?: string;
    productType?: string;
    unit?: string;
    baseUnit?: string;
    stockUnit?: string;
    purchaseUnit?: string;
    packSize?: number;
    conversionFactor?: number;
    allowFractionalQty?: boolean;
  };
};

export type StockTransfer = {
  id: string;
  companyId: string;
  transferNumber: string;
  status: StockTransferStatus;
  fromBranchId: string;
  fromSalesPointId: string;
  toBranchId: string;
  toSalesPointId: string;
  fromWarehouseId?: string | null;
  toWarehouseId?: string | null;
  notes?: string | null;
  items?: StockTransferItem[];
  fromSalesPoint?: { id: string; name: string; code?: string };
  toSalesPoint?: { id: string; name: string; code?: string };
  fromWarehouse?: { id: string; name: string; code?: string; city?: string | null };
  toWarehouse?: { id: string; name: string; code?: string; city?: string | null };
  createdAt?: string;
  dispatchedAt?: string | null;
  receivedAt?: string | null;
};

export type CreateStockTransferPayload = {
  fromSalesPointId: string;
  toSalesPointId: string;
  fromWarehouseId?: string;
  toWarehouseId?: string;
  notes?: string;
  submit?: boolean;
  items: Array<{
    productId: string;
    quantity: number;
    unit?: string;
    packSize?: number;
    conversionFactor?: number;
    notes?: string;
  }>;
};

function unwrapList(data: any): StockTransfer[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.transfers)) return data.transfers;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export const stockTransferAPI = {
  async list(params?: Record<string, unknown>): Promise<{
    transfers: StockTransfer[];
    total: number;
  }> {
    const response = await api.get("/stock-transfers", { params });
    const body = response.data;
    const transfers = unwrapList(body?.data ?? body);
    const total = Number(body?.metaData?.total ?? body?.options?.metaData?.total ?? transfers.length);
    return { transfers, total };
  },

  async getById(id: string): Promise<StockTransfer> {
    const response = await api.get(`/stock-transfers/${id}`);
    return response.data?.data ?? response.data;
  },

  async create(payload: CreateStockTransferPayload): Promise<StockTransfer> {
    const response = await api.post("/stock-transfers", payload);
    return response.data?.data ?? response.data;
  },

  async submit(id: string): Promise<StockTransfer> {
    const response = await api.post(`/stock-transfers/${id}/submit`);
    return response.data?.data ?? response.data;
  },

  async dispatch(id: string): Promise<StockTransfer> {
    const response = await api.post(`/stock-transfers/${id}/dispatch`);
    return response.data?.data ?? response.data;
  },

  async receive(id: string): Promise<StockTransfer> {
    const response = await api.post(`/stock-transfers/${id}/receive`);
    return response.data?.data ?? response.data;
  },

  async cancel(id: string, reason?: string): Promise<StockTransfer> {
    const response = await api.post(`/stock-transfers/${id}/cancel`, { reason });
    return response.data?.data ?? response.data;
  },
};

export default stockTransferAPI;
